import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import AbstractShape from "@/components/AbstractShape";
import { goToIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

export default function About() {
  useSEO({
    title: "About Melbi Wellness | Clinician-Guided Care, Made Simple",
    description:
      "Melbi Wellness makes clinician-guided care simple, transparent, and fully online — with licensed pharmacy fulfillment across all 50 states.",
    url: "https://melbiwellness.com/about",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "About", url: "/about" },
      ]),
    ],
  });
  return (
    <div data-testid="about-page">
      <section className="relative">
        <AbstractShape variant="gradient-warm" colors={["#F5E6D3", "#E8D5C4", "#D4DCD7"]} className="absolute inset-0" testId="about-hero-bg" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">About Melbi</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#2C2B29] max-w-3xl">
            Wellness, made simpler.{" "}
            <span className="italic text-[#C96A4E]">Made transparent.</span>
          </h1>
          <p className="mt-5 text-[#5E5C58] text-lg leading-relaxed max-w-2xl">
            Melbi was created to make wellness access simpler, more transparent, and more guided. No aggressive claims, no confusing treatment menus, and no gray market shortcuts.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <AbstractShape
              variant="gradient-soft-sage"
              colors={["#C8D4CC", "#D4DCD7", "#E8D5C4"]}
              className="aspect-square rounded-3xl"
              testId="about-shape"
            />
          </div>
          <div className="lg:col-span-3 space-y-5 text-[#5E5C58] leading-relaxed">
            <p>
              We believe consumers deserve clear language, clinical responsibility, and a calm, confident experience. Telehealth wellness should feel modern and trustworthy. Not like a med spa, a longevity startup, or a gray market drug brand.
            </p>
            <p>
              Melbi is built around a focused treatment menu, licensed clinician oversight, and licensed pharmacy fulfillment when prescribed. No insurance needed. No insurance friction. No surprises.
            </p>
            <p>
              We will only ever offer treatments where care can be delivered responsibly, and we will be transparent about the limits of what we offer.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">What we believe</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
            Principles for a calmer wellness experience.
          </h2>
        </div>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[
            { t: "Transparent.", b: "Clear pricing, clear treatment menus, and no surprises." },
            { t: "Clinically responsible.", b: "A licensed clinician reviews eligibility, and may say no." },
            { t: "Calm.", b: "No hype, no aggressive claims, no gray market language." },
          ].map((p) => (
            <div key={p.t} className="bg-white rounded-3xl p-6 md:p-8 border border-[#E8E5DF]">
              <h3 className="font-display text-xl font-semibold tracking-tight text-[#2C2B29]">{p.t}</h3>
              <p className="mt-3 text-[#5E5C58] leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
          Ready when you are.
        </h2>
        <p className="mt-3 text-[#5E5C58] max-w-xl mx-auto">
          Begin your clinician-guided wellness journey today.
        </p>
        <button
          data-testid="about-start-now-cta"
          className="mt-6 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-4 text-base font-semibold hover:bg-[#B35A40] transition-colors" type="button" onClick={goToIntake}>
          Start now
          <ArrowRight size={18} className="ml-2" />
        </button>
      </section>
    </div>
  );
}
